import React, {FunctionComponent, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {FormGroup, InputGroup, FileInput, ProgressBar, Toaster, Position, Intent} from '@blueprintjs/core';

import {
  loadView,
  loadViews,
  loadWorkbooks,
  requestTrustedTicket,
  uploadThumbnail
} from '../../../store/actions/tableauActions';
import {IState} from '../../../store/reducers';
import {ApiStatus} from '../../../models';
import {IItem} from '../../select';
import SelectTableauEmbedItem from '../TableauEmbed/SelectTableauEmbedItem';
import {
  createDefaultOption,
  getIntegrationWorkbooks,
  getWorkbookViews,
  removeTrustedTicketFromUrl,
  tableauItemsToOptions,
} from './utils';
import {WORKBOOKS_LIMIT} from './constants';
import EmbedWidget from "./EmbedWidget";
import {useCurrentUser, useCurrentWorkspace} from '../../../hooks';
import {isAdmin} from "../../../utils/auth";
import SettingsCard from './SettingsCard';
import PreviewImage from './PreviewImage';
import {Loading} from '../components';

import './TableauEmbedForm.scss';

interface TableauEmbedFormProps {
  uid: string;
  integrationId: string;
  workbookId: string;
  workbookName: string;
  viewId: string;
  viewName: string;
  filters: string;
  embedUrl: string;
  thumbnail: string;
  onWorkbookChange: (value: IItem) => void;
  onViewChange: (value: IItem) => void;
  onFiltersChange: (value: string) => void;
  onEmbedUrlChange: (value: string) => void;
  onThumbnailChange: (value: string) => void;
  disabled?: boolean;
}

const MAX_SIZE_MB = 5 * 1024 * 1024;

export const thumbnailToaster = Toaster.create({
  position: Position.TOP
});

const TableauEmbedForm: FunctionComponent<TableauEmbedFormProps> = ({
  uid,
  integrationId,
  workbookId,
  workbookName,
  viewId,
  viewName,
  filters,
  embedUrl,
  thumbnail,
  onWorkbookChange,
  onViewChange,
  onFiltersChange,
  onEmbedUrlChange,
  onThumbnailChange,
  disabled = false,
}: TableauEmbedFormProps) => {
  /*
   * Tableau items form (workbooks, views)
   */
  const [loadingWorkbooks, setLoadingWorkbooks] = useState<boolean>(false);
  const [loadingViews, setLoadingViews] = useState<boolean>(false);
  const [loadingView, setLoadingView] = useState<boolean>(false);
  const [loadWorkbookFilter, setLoadWorkbookFilter] = useState(false); // loading action on workbook filter
  const [workbookFiltered, setWorkbookFiltered] = useState(false);
  const [loading, setLoading] = useState(false); // workbook filter loading
  const [loadingTrustedTicket, setLoadingTrustedTicket] = useState(false);
  const [embedUrlComputed, setEmbedUrlComputed] = useState<string>(embedUrl);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [uploadingThumbnail, setUploadingThumbnail] = useState<boolean>(false);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState<string | undefined>(undefined);
  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false);

  const allWorkbooks = useSelector((state: IState) => state.tableau.workbooks);
  const allViews = useSelector((state: IState) => state.tableau.views);
  const loadingWorkbooksStatus = useSelector((state: IState) => state.tableau.loadingWorkbooksStatus);
  const loadingViewsStatus = useSelector((state: IState) => state.tableau.loadingViewsStatus);
  const loadingViewStatus = useSelector((state: IState) => state.tableau.loadingViewStatus);
  const loadingTrustedTicketStatus = useSelector((state: IState) => state.tableau.loadingTrustedTicketStatus);
  const trustedTicket = useSelector((state: IState) => state.tableau.trustedTicket);
  const uploadingThumbnailStatus = useSelector((state: IState) => state.tableau.uploadingThumbnailStatus);
  const uploadedThumbnailUrl = useSelector((state: IState) => state.tableau.thumbnailFile);
  const uploadingThumbnailError = useSelector((state: IState) => state.tableau.thumbnailError);
  const uploadProgress = useSelector((state: IState) => state.tableau.progress);

  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const currentWorkspace = useCurrentWorkspace();

  const workbooks = useMemo(
    () => getIntegrationWorkbooks(allWorkbooks, integrationId),
    [allWorkbooks, integrationId]);
  const views = useMemo(
    () => getWorkbookViews(allViews, integrationId, workbookId),
    [allViews, integrationId, workbookId]);
  const selectedView = useMemo(
    () => views?.find((v: any) => v.id === viewId),
    [views, viewId]
  );
  const showView = useMemo(
    () => (workbooks?.length > 0 || !!viewId),
    [workbooks?.length, viewId]
  );

  const workbooksNo = useRef(0);

  useEffect(() => {
    if (uploadingThumbnailError) {
      thumbnailToaster.show({
        message: uploadingThumbnailError,
        intent: Intent.DANGER,
      });

      setUploadingThumbnail(false);
    }
  }, [uploadingThumbnailError]);

  useEffect(() => {
    if (workbooksNo.current === 0) {
      /*
       * Set only once in order to know if we should use search via API or not
       */
      workbooksNo.current = workbooks?.length || 0;
    }
  }, [workbooks?.length]);

  useEffect(() => {
    if (loadWorkbookFilter && loadingWorkbooksStatus === ApiStatus.LOADED) {
      setLoadWorkbookFilter(false);
    }
  }, [loadWorkbookFilter, loadingWorkbooksStatus]);

  useEffect(() => {
    if (loadingWorkbooks) {
      setLoadingWorkbooks(loadingWorkbooksStatus === ApiStatus.LOADING);
    }
  }, [loadingWorkbooks, loadingWorkbooksStatus]);

  useEffect(() => {
    if (loadingViews) {
      setLoadingViews(loadingViewsStatus === ApiStatus.LOADING);
    }
  }, [loadingViews, loadingViewsStatus]);

  useEffect(() => {
    if (loadingView) {
      setLoadingView(loadingViewStatus === ApiStatus.LOADING);
    }
  }, [loadingView, loadingViewStatus]);

  useEffect(() => {
    if (loadingTrustedTicket) {
      setLoadingTrustedTicket(loadingTrustedTicketStatus === ApiStatus.LOADING);
    }
  }, [loadingTrustedTicket, loadingTrustedTicketStatus]);

  const workbookOptions = useMemo(
    () => tableauItemsToOptions(workbooks),
    [integrationId, workbooks?.length]);
  const viewOptions = useMemo(() =>
      tableauItemsToOptions(views),
    [integrationId, workbookId, views?.length]);

  useEffect(() => {
    if (integrationId && workbookId && viewOptions.length === 0) {
      dispatch(loadViews(uid, integrationId, workbookId));
    }
  }, [integrationId, workbookId, viewOptions.length]);

  const loadWorkbooksAction = useCallback((integrationId: string) => {
    setLoadingWorkbooks(true);
    dispatch(loadWorkbooks(uid as string, integrationId));
  }, [uid]);

  const loadViewsAction = useCallback((integrationId: string, workbookId: string) => {
    setLoadingViews(true);
    dispatch(loadViews(uid as string, integrationId, workbookId));
  }, [uid]);

  const loadViewAction = useCallback((integrationId: string, viewId: string) => {
    setLoadingView(true);
    dispatch(loadView(uid as string, integrationId, viewId));
  }, [uid]);

  useEffect(() => {
    if (uploadingThumbnailStatus === ApiStatus.LOADED) {
      setUploadingThumbnail(false);
      if (uploadedThumbnailUrl) {
        setThumbnailPreviewUrl(uploadedThumbnailUrl);
        onThumbnailChange(uploadedThumbnailUrl);
      }
    } else if (uploadingThumbnailStatus === ApiStatus.FAILED) {
      setUploadingThumbnail(false);
      setThumbnailPreviewUrl(undefined);
    }
  }, [uploadingThumbnailStatus, uploadedThumbnailUrl]);

  // load trusted ticket immediately when component mounts
  useEffect(() => {
    if (integrationId) {
      dispatch(requestTrustedTicket(integrationId));
    }
  }, [integrationId]);

  // make sure we have the view’s metadata
  useEffect(() => {
    if (integrationId && viewId && !selectedView) {
      dispatch(loadView(uid as string, integrationId, viewId));
    }
  }, [integrationId, viewId, selectedView]);

  // re-compute embedUrl when trustedTicket changes
  useEffect(() => {
    const ticketDone =
      loadingTrustedTicketStatus === ApiStatus.LOADED ||
      loadingTrustedTicketStatus === ApiStatus.FAILED;
    const haveView = Boolean(selectedView);

    if (!ticketDone || !haveView) return;

    let url: string;
    if (trustedTicket && trustedTicket !== '-1') {
      const {tableau_url, content_url} = selectedView!;
      const contentPath = content_url.replace('/sheets/', '/');
      url = `${tableau_url}/trusted/${trustedTicket}/views/${contentPath}`;
    } else {
      const {tableau_url, content_url, site_name = ''} = selectedView!;
      const contentPath = content_url.replace('/sheets/', '/');
      url = site_name
        ? `${tableau_url}/#/site/${site_name}/views/${contentPath}`
        : `${tableau_url}/#/views/${contentPath}`;
    }

    setEmbedUrlComputed(url);
  }, [loadingTrustedTicketStatus, trustedTicket, selectedView]);

  const _onWorkbookChange = (value: any) => {
    const workbookId = value.value;

    onWorkbookChange(value);

    if (workbookId) {
      const viewsNo = getWorkbookViews(allViews, integrationId, workbookId);

      if (viewsNo.length === 0) {
        loadViewsAction(integrationId, workbookId);
      }
    }
  };

  const _onViewChange = (value: any) => {
    onViewChange(value);

    if (value.value !== viewId) {
      loadViewAction(integrationId, value.value);
    }
  };

  const _onFiltersChange = (event: any) => {
    onFiltersChange(event.target.value);
  };

  const _onEmbedUrlChange = (event: any) => {
    onEmbedUrlChange(event);
  }

  const shouldLoadWorkbooks = () => {
    setLoading(true);
    if (workbookOptions.length === 0) {
      loadWorkbooksAction(integrationId);
    }
  };

  const shouldLoadViews = () => {
    if (viewOptions.length === 0 && workbookId) {
      loadViewsAction(integrationId, workbookId);
    }
  };

  const onFilterWorkbook = (keyword: string) => {
    let shouldFilter = keyword.length > 2 && workbooksNo.current >= WORKBOOKS_LIMIT;

    if (keyword.length === 0 && workbookFiltered) {
      setWorkbookFiltered(false);
      shouldFilter = true; // filter for empty keyword so we return all the results
    } else if (shouldFilter) {
      setWorkbookFiltered(true);
    }

    if (!shouldFilter) {
      return;
    }
    /*
     * Make an API call only if keyword is longer than 2 characters, and we have more than WORKBOOKS_LIMIT workbooks
     */
    if (keyword.length !== 0) {
      setLoadingWorkbooks(true);
      setLoading(true);
      setLoadWorkbookFilter(true);
      dispatch(loadWorkbooks(uid, integrationId, keyword));
    }
  };

  const onFilterView = () => {
    return false;
  }

  function buildUrlWithoutTrustedTicket() {
    if (!selectedView) {
      return removeTrustedTicketFromUrl(embedUrl);
    }

    const {tableau_url, content_url, site_name} = selectedView;
    const contentUrl = content_url.replace(/\/sheets\//, '/');
    return site_name
      ? `${tableau_url}/#/site/${site_name}/views/${contentUrl}`
      : `${tableau_url}/#/views/${contentUrl}`;
  }

  function buildUrlWithTrustedTicket() {
    if (!selectedView) return embedUrl;

    const {tableau_url, content_url} = selectedView;
    const contentUrl = content_url.replace(/\/sheets\//, '/');
    return `${tableau_url}/trusted/${trustedTicket}/views/${contentUrl}`;
  }

  const _onThumbnailFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];

      if (file.size > MAX_SIZE_MB) {
        thumbnailToaster.show({
          message: `File size exceeds the 5MB limit. Please choose a smaller file.`,
          intent: Intent.DANGER,
        });
        return;
      }

      setThumbnailFile(file);
      setUploadingThumbnail(true);

      const startTime = new Date().getTime().toString();
      const workspaceId = currentWorkspace?.id.toString();

      if (workspaceId) {
        dispatch(uploadThumbnail(integrationId, uid, workspaceId, startTime, file))
      }
    }
  };

  const handleToggleSettingsCard = () => {
    setIsSettingsOpen((prev) => !prev);
  };

  return (
    <div className="tableau-embed-form">
      <div className="tableau-embed-form__content-wrapper">
        {isAdmin(currentUser) && (
          <>
            <div className="tableau-embed-form__settings-card">
              <SettingsCard isOpen={isSettingsOpen} onToggle={handleToggleSettingsCard}>
                <SelectTableauEmbedItem
                  label="Workbook"
                  selected={workbookId}
                  disabled={disabled}
                  onChange={_onWorkbookChange}
                  options={
                    workbookOptions.length > 0 ? workbookOptions : createDefaultOption(workbookName, workbookId)
                  }
                  onFilter={onFilterWorkbook}
                  loading={loading && loadingWorkbooks}
                  showLoadingIcon={loadWorkbookFilter}
                  onClick={shouldLoadWorkbooks}
                />
                {showView && (
                  <>
                    <SelectTableauEmbedItem
                      label="View"
                      selected={viewId}
                      disabled={disabled}
                      onChange={_onViewChange}
                      options={
                        viewOptions.length > 0 ? viewOptions : createDefaultOption(viewName, viewId)
                      }
                      onFilter={onFilterView}
                      loading={loadingViews}
                      onClick={shouldLoadViews}
                    />
                    <FormGroup inline={true} label="Filters">
                      <InputGroup
                        defaultValue={filters || ''}
                        className="tableau-embed-form__input"
                        onChange={_onFiltersChange}
                        autoComplete="off"
                        placeholder="Filter1=Value1&..."
                        disabled={disabled}
                      />
                    </FormGroup>
                    <FormGroup inline={true} label="Thumbnail File">
                      <div className="tableau-embed-form__fileinput-wrapper">
                        <FileInput
                          text={thumbnailFile ? thumbnailFile.name : "Choose file... (max 5MB)"}
                          className="tableau-embed-form__fileinput"
                          onInputChange={_onThumbnailFileChange}
                          inputProps={{accept: 'image/*'}}
                          disabled={disabled}
                        />
                        {uploadingThumbnail && (
                          <ProgressBar intent="primary" value={uploadProgress / 100}/>
                        )}
                      </div>
                    </FormGroup>
                  </>
                )}
              </SettingsCard>
            </div>
            {isSettingsOpen && (
              <div className="tableau-embed-form__preview-image">
                <PreviewImage
                  image={thumbnailPreviewUrl}
                  loading={uploadingThumbnail}
                  thumbnail={thumbnail}
                />
              </div>
            )}
          </>
        )}
      </div>
      <div className="tableau-embed-form__widget">
        {embedUrlComputed ? (
          <EmbedWidget
            loading={loadingView}
            embedUrl={embedUrlComputed}
            filters={filters}
            onChange={_onEmbedUrlChange}
          />
        ) : (
          <Loading/>
        )}
      </div>
    </div>
  );

};

export default TableauEmbedForm;
